import React, {Fragment, useCallback, useEffect, useState} from "react";
import {useNavigate, useParams, useSearchParams} from "react-router-dom";
import authService from '../../services/AuthService';
import RelayListControl from "../../components/menu/RelaySettings/RelayListControl";
import BlocksControl from "../../components/menu/BlocksControl/BlocksControl";
import TabItem from "../../components/shared/Tabs/TabItem";
import TabList from "../../components/shared/Tabs/TabList";
import ExternalDevices from "../../components/menu/ExternalDevices/ExternalDevices";
import ExternalDevicesTest from "../../components/menu/ExternalDevices-test/ExternalDevicesTest";
import SynchronizationControl from "../../components/menu/PrimaryClock/components/Synchronization/SynchronizationControl";
import './Device.scss';
import DeviceLogs from "../../components/menu/DeviceLogs/DeviceLogs";
import Journal from "../../components/menu/Journal/Journal";
import {Message, toaster} from "rsuite";
import DevicePanel from "../../components/DevicePanel/DevicePanel";
import MainPage from "../../components/menu/MainPage/MainPage";
import EspTime from "../../components/menu/PrimaryClock/components/EspTime/EspTime";
import UsbSettings from "../../components/menu/PrimaryClock/components/UsbSettings/UsbSettings";
import CoordinatesBks from "../../components/menu/ExternalDevices/components/CoordinatesBks/CoordinatesBks";
import SecondaryClock from "../../components/menu/SecondaryClock/SecondaryClock";
import RelaySettings from "../../components/menu/RelaySettings/RelaySettings";
import {DuskDawnTimeResponse} from "../../components/menu/RelaySettings/RelaySettings.model";
import ClockChimeSettings from "../../components/menu/ClockChimeSettings/ClockChimeSettings";
import NetworkSettings from "../../components/menu/NetworkSettings/NetworkSettings";
import {RelaySettingsApi} from "../../services/RelaySettings.api";
import SystemSettings from "../../components/menu/SystemSettings/SystemSettings";
import BroadcastCommands from "../../components/menu/BroadcastCommands/BroadcastCommands";
import PrimaryClock from "../../components/menu/PrimaryClock/PrimaryClock";
import { getDeviceTabConfig } from "../../components/shared/Tabs/deviceTabsConfig";
import TestComponent from "../../components/TestComponent/TestComponent";
import {
  CommandParameters,
  CommandQueryResponse,
  DeviceData,
  DeviceTab,
  RouteParams,
  UserStorageData
} from "./Device.model";
import { DeviceUtils } from "./Device.utils";
import {SharedApi} from "../../services/Shared.api";
import type { DevicePanelDevice } from "../../components/DevicePanel/DevicePanel.model";
import { clearRequestCache } from "../../api/requestCache";
import MaintenanceChatNotice from '../../components/shared/VerboxChat/MaintenanceChatNotice';

require('moment/locale/ru');

type CachedCommandQueryValue = {
  response: CommandQueryResponse;
  parsed: unknown;
};

type CommandCacheEntry = {
  value: CachedCommandQueryValue;
  expiresAt: number;
  generation: number;
};

type PendingCommandCacheEntry = {
  promise: Promise<CachedCommandQueryValue>;
  generation: number;
};

const COMMAND_QUERY_CACHE_TTL_MS = 1500;
const commandResponseCache = new Map<string, CommandCacheEntry>();
const commandPendingCache = new Map<string, PendingCommandCacheEntry>();
let commandCacheGeneration = 0;

const isDev = typeof process !== "undefined" && process.env.NODE_ENV !== "production";

const logCommandCache = (...args: unknown[]) => {
  if (isDev) {
    console.debug("[command-cache]", ...args);
  }
};

const isReadOnlyCommand = (command: unknown) => {
  return typeof command === "string" && /^(Read|read)/.test(command);
};

const buildCommandCacheKey = (
  authToken: string,
  requestData: Record<string, unknown>
) => {
  return JSON.stringify({
    authToken,
    ...requestData
  });
};

const getCachedCommandQueryValue = (key: string): CachedCommandQueryValue | undefined => {
  const entry = commandResponseCache.get(key);

  if (!entry) {
    return undefined;
  }

  if (entry.generation !== commandCacheGeneration || entry.expiresAt <= Date.now()) {
    commandResponseCache.delete(key);
    return undefined;
  }

  logCommandCache("cache hit", key);
  return entry.value;
};

const setCachedCommandQueryValue = (key: string, value: CachedCommandQueryValue): void => {
  commandResponseCache.set(key, {
    value,
    expiresAt: Date.now() + COMMAND_QUERY_CACHE_TTL_MS,
    generation: commandCacheGeneration
  });
};

const getPendingCommandQueryValue = (key: string): Promise<CachedCommandQueryValue> | undefined => {
  const entry = commandPendingCache.get(key);

  if (!entry || entry.generation !== commandCacheGeneration) {
    commandPendingCache.delete(key);
    return undefined;
  }

  logCommandCache("pending hit", key);
  return entry.promise;
};

const setPendingCommandQueryValue = (key: string, promise: Promise<CachedCommandQueryValue>): void => {
  commandPendingCache.set(key, {
    promise,
    generation: commandCacheGeneration
  });
};

const removePendingCommandQueryValue = (key: string, promise?: Promise<CachedCommandQueryValue>): void => {
  const entry = commandPendingCache.get(key);

  if (!entry) {
    return;
  }

  if (!promise || entry.promise === promise) {
    commandPendingCache.delete(key);
  }
};

const clearCommandQueryCache = (reason?: string): void => {
  if (commandResponseCache.size || commandPendingCache.size) {
    logCommandCache("clear", reason || "manual");
  }

  commandResponseCache.clear();
  commandPendingCache.clear();
  commandCacheGeneration += 1;
};

const Device = () => {
  const {deviceId} = useParams<RouteParams>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const currentTab = (searchParams.get('tab') || 'main') as DeviceTab;
  const externalId = searchParams.get('externalId');

  const [loading, setLoading] = useState(true);
  const [device, SetDevice] = useState<DeviceData | null>(null);
  const [isNewVersion, setIsNewVersion] = useState<boolean | undefined>(undefined);
  const [commandResult, SetCommandResult] = useState("");

  const [availableDevices, setAvailableDevices] = useState<DeviceData[]>([]);
  const [loadingDevices, setLoadingDevices] = useState(true);
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
  const [isClockStation, setIsClockStation] = useState<boolean | null>(null);
  const [dawnTime, setDawnTime] = useState("");
  const [duskTime, setDuskTime] = useState("");

  const TAB_CONFIG = getDeviceTabConfig(isClockStation, isAdmin);


  const handleTabChange = useCallback((tabKey: DeviceTab) => {
    const url = DeviceUtils.buildDeviceTabUrl(deviceId, tabKey);
    navigate(url, {replace: true});
  }, [deviceId, navigate]);

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('user') || 'null') as UserStorageData | null;
    setIsAdmin(user?.isAdmin ?? null);
    if (!device) {
      setIsClockStation(null);
      return;
    }
    setIsClockStation(device?.clockStation != null);
  }, [device, device?.clockStation]);

  useEffect(() => {
    if (deviceId) {
      localStorage.setItem("lastDeviceId", deviceId);
    }
  }, [deviceId]);

  useEffect(() => {
    if (isClockStation !== null) {
      localStorage.setItem("lastDeviceIsClockStation", String(isClockStation));
    }
  }, [isClockStation]);


  const getDuskDawnTime = async () => {
    try {
      const response: DuskDawnTimeResponse = await RelaySettingsApi.getDuskDawnTime(deviceId as string);

      setDuskTime(response.onTime?.replace(/:\d{2}$/, "") || "");
      setDawnTime(response.offTime?.replace(/:\d{2}$/, "") || "");
    } catch (e) {
      console.error(e);
      setDuskTime("");
      setDawnTime("");
    }
  };

  const refreshDeviceData = async () => {
    try {
      const updatedDevice = await getDeviceInfoAsync();
      SetDevice(updatedDevice ?? null);

      toaster.push(
        <Message type="success" closable>
          Данные устройства обновлены
        </Message>,
        {placement: 'topEnd', duration: 2000}
      );
    } catch (error) {
      console.error('Ошибка при обновлении устройства:', error);
    }
  };

  useEffect(() => {
    if (deviceId) {
      const updateDeviceDataAsync = async () => {
        const device = await getDeviceInfoAsync();
        SetDevice(device ?? null);

        try {
          const firmwareVersion = device?.clockStation?.firmwareVersion?.trim();
          setIsNewVersion(DeviceUtils.getIsNewVersion(firmwareVersion));
        } catch (e) {
          console.error('Ошибка при обработке версии прошивки:', e);
          setIsNewVersion(false);
        }
      };

      updateDeviceDataAsync();
    }
  }, [deviceId]);

  useEffect(() => {
    if (deviceId && device?.clockStation != null) {
      getDuskDawnTime().then();
    } else if (device?.clockStation === null) {
      setDuskTime("");
      setDawnTime("");
    }
  }, [deviceId, device?.clockStation]);

  const handleRefreshDeviceList = async () => {
    try {
      await loadAvailableDevices();
      toaster.push(
        <Message type="success">Список устройств обновлен</Message>,
        {placement: 'topCenter', duration: 2000}
      );
    } catch (error) {
      console.error('Ошибка при обновлении списка устройств:', error);
    }
  };

  useEffect(() => {
    let interval: ReturnType<typeof setInterval> | null = null;
    if (!loading && deviceId) {
      interval = setInterval(async () => {
        const device = await getDeviceInfoAsync();
        SetDevice(device ?? null);
      }, 60000);
    } else if (interval) {
      clearInterval(interval);
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [loading, deviceId]);

  const handleRefreshDevice = async () => {
    try {
      const updatedDevice = await getDeviceInfoAsync();
      SetDevice(updatedDevice ?? null);

      toaster.push(
        <Message type="success" closable>
          Данные устройства обновлены
        </Message>,
        {placement: 'topCenter', duration: 2000}
      );
    } catch (error) {
      console.error('Ошибка при обновлении устройства:', error);
      toaster.push(
        <Message type="error" closable>
          Ошибка при обновлении устройства
        </Message>,
        {placement: 'topCenter', duration: 2000}
      );
    }
  };

  const getDeviceInfoAsync = async (): Promise<DeviceData | undefined> => {
    if (!deviceId) return;

    try {
      const response = await SharedApi.getControlBlockInfo(deviceId);
      return response ?? undefined;
    } catch (error) {
      console.error(error);
      return undefined;
    } finally {
      setLoading(false);
    }
  };

  const loadAvailableDevices = async (): Promise<DeviceData[]> => {
    try {
      const response = await SharedApi.getControlBlocks()

      if (response) {
        return response
      } else {
        return [];
      }
    } catch (error) {
      console.error('Ошибка при загрузке списка устройств:', error);
      return [];
    }
  };

  const enrichSingleDevice = async (device: DeviceData): Promise<DeviceData> => {

    if (!device || !device.id) return device;

    try {
      const status = await getStatusInfo(device.id)
      return DeviceUtils.enrichDevice(device, status, dawnTime, duskTime);
    } catch (error) {
      console.error(`Ошибка при загрузке данных для устройства ${device.id}:`, error);
      return DeviceUtils.enrichDeviceWithFallback(device);
    }
  };

  const enrichAllDevices = async (devices: DeviceData[]): Promise<DeviceData[]> => {
    if (!devices || devices.length === 0) return devices;

    const enrichedDevices = [...devices];

    const enrichmentPromises = devices.map(async (device, index) => {
      try {
        const status = await getStatusInfo(device.id ?? "")
        enrichedDevices[index] = DeviceUtils.enrichDevice(device, status, dawnTime, duskTime);
      } catch (error) {
        console.error(`Ошибка при загрузке данных для устройства ${device.id}:`, error);
        enrichedDevices[index] = DeviceUtils.enrichDeviceWithFallback(device);
      }
    });

    await Promise.all(enrichmentPromises);
    return enrichedDevices;
  };

  const runCommandQuery = useCallback(async (
    requestData: Record<string, unknown>,
    command: unknown
  ) => {
    const isCacheable = isReadOnlyCommand(command);
    const accessToken = (await authService.isAuthenticated())
      ? ((await authService.getAccessToken()) || "")
      : "";
    const cacheKey = isCacheable ? buildCommandCacheKey(accessToken, requestData) : null;

    if (!isCacheable) {
      clearCommandQueryCache(`write ${String(command)}`);
      clearRequestCache(`command write ${String(command)}`);
    } else if (cacheKey) {
      const cachedValue = getCachedCommandQueryValue(cacheKey);
      if (cachedValue) {
        SetCommandResult(JSON.stringify(cachedValue.response));
        return cachedValue.parsed;
      }

      const pendingValue = getPendingCommandQueryValue(cacheKey);
      if (pendingValue) {
        const resolvedValue = await pendingValue;
        SetCommandResult(JSON.stringify(resolvedValue.response));
        return resolvedValue.parsed;
      }
    }

    SetCommandResult("Loading");

    const requestOptions = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`
      },
      body: JSON.stringify(requestData)
    };

    const requestPromise = (async () => {
      const response = await fetch('/api/commands/Query', requestOptions);
      const result = await response.json() as CommandQueryResponse;
      const parsed = JSON.parse(result.data as string);

      return {
        response: result,
        parsed
      } as CachedCommandQueryValue;
    })();

    if (cacheKey) {
      setPendingCommandQueryValue(cacheKey, requestPromise);
      requestPromise
        .finally(() => removePendingCommandQueryValue(cacheKey, requestPromise))
        .catch(() => undefined);
    }

    try {
      const value = await requestPromise;

      if (cacheKey) {
        setCachedCommandQueryValue(cacheKey, value);
      }

      SetCommandResult(JSON.stringify(value.response));
      return value.parsed;
    } catch (error) {
      SetCommandResult("Error");
      throw error;
    }
  }, []);

  const executeCommand = useCallback(async (device: unknown, command: unknown, parameters: CommandParameters) => {
    const base64EncodedString = DeviceUtils.buildBase64StringFromBytes(parameters.map(x => Number(x)));

    const requestData = {
      deviceId: deviceId,
      device: device,
      command: command,
      params: base64EncodedString
    };

    return runCommandQuery(requestData, command);
  }, [deviceId, runCommandQuery]);

  const executeDeviceCommand = useCallback(async (
    devAdr: number | string,
    devType: number | string,
    command: unknown,
    parameters: CommandParameters
  ) => {
    const base64EncodedString = DeviceUtils.buildBase64StringFromBytes(parameters.map(x => Number(x)));

    const requestData = {
      deviceId: deviceId,
      device: "Device485",
      command: command,
      params: base64EncodedString,
      devAdr: devAdr,
      devType: devType
    };

    return runCommandQuery(requestData, command);
  }, [deviceId, runCommandQuery]);

  const handleDeviceSelect = (selectedDevice: DevicePanelDevice) => {
    navigate(`/devices/${selectedDevice.id}`);
  };

  const getStatusInfo = useCallback(async (deviceGuid: string | number) => {
    try {
      const response = await SharedApi.getControlBlocksStatus(String(deviceGuid));
      return await response
    } catch (err) {
      console.error('Ошибка при получении статуса устройства:', err);
      return {isConnected: false};
    }
  }, []);

  const refreshDuskDawn = async () => {
    if (!deviceId) return;
    await getDuskDawnTime();
  };

  useEffect(() => {
    const loadDevices = async () => {
      if (isAdmin === null) {
        return;
      }

      setLoadingDevices(true);

      if (isAdmin) {
        if (!device || String(device.id) !== String(deviceId)) {
          setAvailableDevices([]);
          setLoadingDevices(false);
          return;
        }

        try {
          const enrichedCurrentDevice = await enrichSingleDevice(device);
          setAvailableDevices([enrichedCurrentDevice]);
        } catch (error) {
          console.error('Ошибка при загрузке текущего устройства:', error);
          setAvailableDevices([]);
        }
      } else {
        try {
          const devices = await loadAvailableDevices();
          if (devices && devices.length > 0) {
            const enrichedDevices = await enrichAllDevices(devices);
            setAvailableDevices(enrichedDevices);
          } else {
            setAvailableDevices([]);
          }
        } catch (error) {
          console.error('Ошибка при загрузке списка устройств:', error);
          setAvailableDevices([]);
        }
      }
      setLoadingDevices(false);
    };

    loadDevices();
  }, [deviceId, isAdmin, device]);





  const renderTabContent = (tabKey: DeviceTab) => {
    switch (tabKey) {
      case 'main':
        return (
          <MainPage
            dawnTime={dawnTime}
            duskTime={duskTime}
            channels={device?.clockStation?.channels}
            executeCommand={executeCommand as any}
            deviceId={deviceId as any}
            onTabChange={handleTabChange}
            isNewVersion={isNewVersion as any}
            device={availableDevices[0] as any}
          />
        );
      case 'primary':
        return (
          <>
            {isClockStation && (
              <PrimaryClock
                duskTime={duskTime}
                dawnTime={dawnTime}
                deviceId={deviceId as string}
                getDuskDawnTime={getDuskDawnTime}
                executeCommand={executeCommand as any}/>
            )}
            <EspTime deviceId={deviceId as string}/>
            {!device?.clockStation?.firmwareVersion?.toLowerCase().includes('iastra') && (
              <UsbSettings deviceId={deviceId as string}/>
            )}
            <SynchronizationControl
              executeCommand={executeCommand as any}
              clockStationId={deviceId as string}
              isClockStation={Boolean(isClockStation)}
            />
            <CoordinatesBks deviceId={deviceId as string}/>
          </>
        );
      case 'secondary':
        if (!isClockStation) return null;
        return (
          <SecondaryClock
            deviceId={deviceId as string}
            isAdmin={Boolean(isAdmin)}
            channels={device?.clockStation?.channels}
          />
        );
      case 'relay':
        if (!isClockStation) return null;
        return (
          <>
            <RelaySettings
              dawnTime={dawnTime}
              duskTime={duskTime}
              deviceId={deviceId as string}
              withMelody={Boolean(device?.clockStation?.withMelody)}
            />
            <RelayListControl
              deviceId={deviceId as any}
              executeCommand={executeCommand as any}
              dawnTime={dawnTime}
              duskTime={duskTime}
              onAfterTimeRelayWrite={refreshDuskDawn}
            />
          </>
        );
      case 'chimes':
        if (!isClockStation || !device?.clockStation?.withMelody) return null;
        return (
          <>
            <ClockChimeSettings
              isAdmin={Boolean(isAdmin)}
              deviceId={deviceId as string}
              withCustomMusic={device?.clockStation?.withCustomMelodyEffect ?? false}
            />
          </>

        )
      case 'devices':
        return (
          <ExternalDevices
            deviceId={deviceId as string}
            selectedDeviceId={externalId}
            executeCommand={executeCommand as any}
            executeDeviceCommand={executeDeviceCommand as any}
            clockStationId={deviceId as string}
            isAdmin={Boolean(isAdmin)}
          />
        );
      // case 'devices-test':
      //   return (
      //     <ExternalDevicesTest
      //       deviceId={deviceId as string}
      //       executeCommand={executeCommand as any}
      //       clockStationId={deviceId as string}
      //       isAdmin={Boolean(isAdmin)}
      //     />
      //   );
      case 'network':
        return (
          <NetworkSettings deviceId={deviceId as string} executeCommand={executeCommand as any}/>
        );
      case 'system':
        return (
          <>
            <SystemSettings
              device={device as any}
              getDeviceData={refreshDeviceData}
              executeCommand={executeCommand as any}
              isAdmin={Boolean(isAdmin)}/>
            <hr className="h-divider"/>
          </>
        );
      case 'broadcast-commands':
        return (
          <>
            <BroadcastCommands deviceId={deviceId as string} />
            <hr className="h-divider"/>
          </>
        );
      case 'blocks':
        if (!(isAdmin && isClockStation)) return null;
        return (
          <>
            <hr className="h-divider"/>
            <BlocksControl deviceId={deviceId as string} executeCommand={executeCommand as any}/>
          </>
        );
      case 'logs':
        if (!isAdmin) return null;
        return (
          <DeviceLogs deviceId={deviceId as string}/>
        );
      case 'journal':
        if (!isAdmin) return null;
        return (
          <Journal deviceId={deviceId as string}/>
        );
      case 'test':
        return (
          <TestComponent deviceId={deviceId as string}/>
        );
      default:
        return null;
    }
  };

  const content = () => {
    const devicePanel = (
      <div className='mt-2 mb-0'>
        <DevicePanel
          device={(DeviceUtils.getSelectedDeviceForPanel(isAdmin, availableDevices, device) as any)}
          dawnTime={dawnTime}
          duskTime={duskTime}
          onDeviceSelect={handleDeviceSelect}
          availableDevices={availableDevices as any}
          onRefreshDevice={handleRefreshDevice}
          isAdmin={Boolean(isAdmin)}
          executeCommand={executeCommand as any}
        />
      </div>
    );

    return (
      <Fragment>
        {devicePanel}
        <MaintenanceChatNotice key={deviceId} device={device} deviceId={deviceId} />

        {device && isClockStation !== null && isAdmin !== null && (
          <TabList
            activeTabKey={currentTab}
            onTabChange={handleTabChange}
          >
            {TAB_CONFIG.map(({key, label, condition}) =>
                condition && (
                  <TabItem key={key} tabKey={key} label={label}>
                    {renderTabContent(key)}
                  </TabItem>
                )
            )}
          </TabList>
        )}


      </Fragment>
    );
  };

  const render = () => {
    return (
      <>
        {
          loading
            ? <div className="loader-wrapper"></div>
            : content()
        }
      </>
    );
  };

  return render();
};

export default Device;
